import { h } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import store from "./store/index.js";
import { getTeacherCodeAccess } from "./utils/teacherCodeAccess.js";

import DashboardStats from "./components/DashboardStats.vue";
import GroupDashboardStats from "./components/GroupDashboardStats.vue";

import LoginPage from "./pages/LoginPage.vue";
import SignupPage from "./pages/SignupPage.vue";
import HomePage from "./pages/HomePage.vue";
import CategoryPage from "./pages/CategoryPage.vue";
import LevelPage from "./pages/LevelPage.vue";

function capitalize(str = "") {
  const safeStr = String(str);
  return safeStr.charAt(0).toUpperCase() + safeStr.slice(1);
}

function normalizeRole(role) {
  return typeof role === "string" ? role.trim().toLowerCase() : "";
}

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatDateToYMD(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
}

function getTodayYMD() {
  return formatDateToYMD(new Date());
}

function normalizeActiveUntil(value) {
  if (!value) return "";

  if (typeof value?.toDate === "function") {
    return formatDateToYMD(value.toDate());
  }

  if (value instanceof Date) {
    return formatDateToYMD(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }

    const parsed = new Date(trimmed);
    return formatDateToYMD(parsed);
  }

  return "";
}

function isActiveUntilCurrentOrFuture(activeUntil) {
  const normalized = normalizeActiveUntil(activeUntil);
  return normalized ? normalized >= getTodayYMD() : false;
}

function getEffectiveActiveUntil(userData = {}) {
  const role = normalizeRole(userData?.role);

  const accountActiveUntil = normalizeActiveUntil(userData?.accountActiveUntil);
  const shallowActiveUntil = normalizeActiveUntil(userData?.activeUntil);
  const activeKidActiveUntil = normalizeActiveUntil(
    userData?.activeKid?.activeUntil
  );

  const kids = Array.isArray(userData?.kids) ? userData.kids : [];
  const activeKidId =
    typeof userData?.activeKidId === "string" ? userData.activeKidId : "";

  const selectedKid =
    kids.find((kid) => kid?.kidId && kid.kidId === activeKidId) ||
    kids[0] ||
    null;

  const selectedKidActiveUntil = normalizeActiveUntil(
    selectedKid?.activeUntil
  );

  if (role === "teacher") {
    return (
      accountActiveUntil ||
      shallowActiveUntil ||
      activeKidActiveUntil ||
      selectedKidActiveUntil
    );
  }

  return (
    activeKidActiveUntil ||
    selectedKidActiveUntil ||
    shallowActiveUntil ||
    accountActiveUntil
  );
}

const DashboardPageComponent = {
  name: "DashboardPageComponent",
  render() {
    const userRole = normalizeRole(store.getters["auth/userData"]?.role);
    const componentToRender =
      userRole === "teacher" ? GroupDashboardStats : DashboardStats;

    return h(componentToRender);
  },
};

const routes = [
  {
    path: "/login",
    name: "Login",
    component: LoginPage,
    meta: { title: "Login" },
  },
  {
    path: "/signup",
    name: "Signup",
    component: SignupPage,
    meta: { title: "Sign Up" },
  },
  {
    path: "/",
    name: "Home",
    component: HomePage,
    meta: { title: "Home" },
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: DashboardPageComponent,
    meta: { title: "Dashboard" },
  },
  {
    path: "/group-dashboard",
    redirect: { name: "Dashboard" },
  },
  {
    path: "/group-daskboard",
    redirect: { name: "Dashboard" },
  },
  {
    path: "/:category",
    name: "CategoryPage",
    component: CategoryPage,
    props: true,
    meta: {
      title: (route) =>
        `${capitalize(route.params.category)} - Category - Vocablly`,
    },
  },
  {
    path: "/:category/:level",
    name: "LevelPage",
    component: LevelPage,
    props: true,
    meta: {
      title: (route) =>
        `Level ${route.params.level} - ${capitalize(
          route.params.category
        )} - Vocablly`,
    },
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

const freeCategories = new Set(["letters", "numbers"]);

const publicPages = new Set(["Login", "Signup"]);

const authOnlyPages = new Set(["Dashboard"]);

router.beforeEach(async (to, from, next) => {
  try {
    const metaTitle = to.meta?.title;

    if (typeof metaTitle === "function") {
      document.title = metaTitle(to);
    } else if (metaTitle) {
      document.title = metaTitle;
    } else {
      document.title = "Vocablly";
    }

    if (!store.state.auth?.authReady) {
      await store.dispatch("auth/initAuthentication");
    }

    const isLoggedIn = !!store.getters["auth/loggedInUser"];
    const userData = store.getters["auth/userData"] || {};

    const effectiveActiveUntil = getEffectiveActiveUntil(userData);
    const hasActiveSubscription =
      isActiveUntilCurrentOrFuture(effectiveActiveUntil);

    const teacherCodeAccess = getTeacherCodeAccess();
    const hasTeacherCodeAccess = !!teacherCodeAccess;
    const hasAppAccess = isLoggedIn || hasTeacherCodeAccess;

    if (publicPages.has(to.name)) {
      if (hasAppAccess) {
        return next("/");
      }

      return next();
    }

    if (to.name === "Home") {
      return next();
    }

    const category =
      typeof to.params.category === "string"
        ? to.params.category.toLowerCase()
        : null;

    if (category && freeCategories.has(category)) {
      return next();
    }

    if (authOnlyPages.has(to.name)) {
      if (hasTeacherCodeAccess) {
        return next("/");
      }

      if (!isLoggedIn) {
        return next("/login");
      }

      return next();
    }

    if (!hasAppAccess) {
      return next("/login");
    }

    if (hasTeacherCodeAccess) {
      return next();
    }

    if (!hasActiveSubscription) {
      return next("/dashboard");
    }

    return next();
  } catch (error) {
    console.error("Navigation guard error:", error);
    return next("/");
  }
});

export default router;